import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  House, FileText, Users, FileMagnifyingGlass, ClipboardText,
  ChartBar, SignOut, List, CaretLeft, CaretRight, Bell,
  MagnifyingGlass, CalendarBlank, Gear, Notebook
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const ROLE_LABELS = {
  requestor: 'Requestor',
  supervisor: 'Supervisor',
  ap_accounting: 'AP Accounting',
  ap_supervisor: 'AP Supervisor',
  accounting_manager: 'Accounting Manager',
  general_manager: 'General Manager',
  owner: 'Owner'
};

const NAV_ITEMS = {
  requestor: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'My Requests' },
    { to: '/payments/create', icon: ClipboardText, label: 'New Request' },
  ],
  ap_supervisor: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'Review Queue' },
    { to: '/reports', icon: Notebook, label: 'Reports' },
  ],
  supervisor: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'Supervisor Review Queue' },
    { to: '/reports', icon: Notebook, label: 'Reports' },
  ],
  ap_accounting: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'Payment Requests' },
    { to: '/payments/create', icon: ClipboardText, label: 'New Request' },
    { to: '/vendors', icon: Users, label: 'Vendors' },
    { to: '/contracts', icon: FileMagnifyingGlass, label: 'Contracts' },
    { to: '/documents', icon: FileMagnifyingGlass, label: 'Document Search' },
    { to: '/upcoming', icon: CalendarBlank, label: 'Upcoming Payments' },
    { to: '/reports', icon: Notebook, label: 'Reports' },
  ],
  accounting_manager: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'All Requests' },
    { to: '/vendors', icon: Users, label: 'Vendors' },
    { to: '/contracts', icon: FileMagnifyingGlass, label: 'Contracts' },
    { to: '/upcoming', icon: CalendarBlank, label: 'Upcoming Payments' },
    { to: '/analytics', icon: ChartBar, label: 'Analytics' },
    { to: '/reports', icon: Notebook, label: 'Reports' },
    { to: '/admin/setup', icon: Gear, label: 'Admin Setup' },
  ],
  general_manager: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'All Requests' },
    { to: '/vendors', icon: Users, label: 'Vendors' },
    { to: '/contracts', icon: FileMagnifyingGlass, label: 'Contracts' },
    { to: '/upcoming', icon: CalendarBlank, label: 'Upcoming Payments' },
    { to: '/analytics', icon: ChartBar, label: 'Analytics' },
    { to: '/reports', icon: Notebook, label: 'Reports' },
  ],
  owner: [
    { to: '/', icon: House, label: 'Dashboard' },
    { to: '/payments', icon: FileText, label: 'All Requests' },
    { to: '/vendors', icon: Users, label: 'Vendors' },
    { to: '/contracts', icon: FileMagnifyingGlass, label: 'Contracts' },
    { to: '/upcoming', icon: CalendarBlank, label: 'Upcoming Payments' },
    { to: '/analytics', icon: ChartBar, label: 'Analytics' },
    { to: '/reports', icon: Notebook, label: 'Reports' },
    { to: '/audit', icon: List, label: 'Audit Trail' },
    { to: '/admin/setup', icon: Gear, label: 'Admin Setup' },
  ]
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navItems = NAV_ITEMS[user?.role] || [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`flex flex-col border-r border-border bg-surface transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ backgroundColor: '#FFFFFF' }}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!collapsed && (
            <span className="font-heading text-lg font-bold tracking-tight text-primary">
              RFP System
            </span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded-sm hover:bg-muted transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
          </button>
        </div>

        <nav className="flex-1 py-2 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-[#0055FF]/10 text-[#0055FF] font-medium border-r-2 border-[#0055FF]'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                } ${collapsed ? 'justify-center px-2' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} weight="regular" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          {!collapsed && (
            <div className="mb-2 px-1">
              <p className="text-xs font-medium text-primary truncate">{user?.full_name}</p>
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-3 border-b border-border bg-white" data-testid="app-header">
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search requests, vendors, contracts..."
                className="pl-9 h-8 text-sm rounded-sm"
                data-testid="global-search-input"
              />
            </div>
          </form>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-sm gap-2" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-sm bg-[#0055FF] text-white flex items-center justify-center text-xs font-bold">
                    {user?.full_name?.charAt(0)}
                  </div>
                  {user?.full_name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <SignOut size={16} className="mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: '#F8FAFC' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
